import { Button, Flex, FormGroup, FormLabel, Link } from "@eworkit/iris";
import { Formik, FormikProps } from "formik";
import React, { FC, useCallback, useContext, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link as ReactRouterLink } from "react-router-dom";
import * as Yup from "yup";

import { FormikTextInput, VALIDATION_TYPES } from "@/components/Formik";
import { FormikError } from "@/components/Formik";
import { Header } from "@/components/Header";
import { Section } from "@/components/Section";
import { FormikSelect } from "@/components/Select";
import { Text } from "@/components/Text";
import { UserContext } from "@/components/User";
import { IUserEmail } from "@/data/session";
import { IModalContainer, inDialogModal, MessagesContext } from "@/eworkitComponents";
import { FormikForm } from "@/formikComponents/FormikForm";
import { CloseButton } from "@/views/components/CloseButton";
import { Messenger } from "@/views/components/Messenger";
import { router } from "@/views/router";

import "./_email_handling_form.scss";

const MODAL_MESSENGER_CONTEXT = "EMAIL_HANDLING_MODAL_CONTEXT";

interface IEmailHandlingModalProps extends IModalContainer {
  hasEmail?: boolean;
  hasPassword?: boolean;
  afterSubmit: () => void;
  onSubmit: (data: IEmailHandlingFormValues) => void;
  variant: "ADD_EMAIL" | "REMOVE" | "MAKE_PRIMARY" | "ASSIGN_TO_CONSULTANT";
  email?: string;
  emailVariant?: "INPUT" | "SELECT";
}

export interface IEmailHandlingFormValues {
  email: string;
  password: string;
}

const EmailHandlingModalComponent: FC<IEmailHandlingModalProps> = ({
  onClose,
  email = "",
  hasEmail = false,
  hasPassword = true,
  variant,
  onSubmit,
  afterSubmit,
  emailVariant = "INPUT",
}) => {
  const { t } = useTranslation("session");
  const baseClass = "email_handling-form";
  const formRef = useRef<FormikProps<any> | null>(null);
  const { addMessage } = useContext(MessagesContext);
  const User = useContext(UserContext);
  const [newEmailOption, setNewEmailOption] = useState<Partial<IUserEmail> | null>(null);

  const initialValues: IEmailHandlingFormValues = {
    password: "",
    email: hasEmail ? "" : email,
  };

  const EMAIL_OVERRIDE_MESSAGES = {
    ALREADY_EXISTS: t(`USERS.SETTINGS.USER_ACCOUNT.EMAILS.ERRORS.ALREADY_EXISTS`),
  };

  const handleSubmit = useCallback(async data => {
    try {
      await onSubmit(data);
      afterSubmit();
    } catch (e) {
      const { payload } = e;

      switch (payload.code) {
        case "INVALID_PASSWORD": {
          formRef.current?.setFieldError("password", payload.code);
          break;
        }
        case "ALREADY_EXISTS": {
          formRef.current?.setFieldError("email", payload.code);
          break;
        }
        case "NOT_VALID_PARAM": {
          payload.errors.forEach((e: { field: string; code: string }) => {
            formRef.current?.setFieldError(e.field, e.code);
          });
          break;
        }
        case "EMAIL_IN_USE": {
          addMessage({
            context: MODAL_MESSENGER_CONTEXT,
            text: t(`USERS.SETTINGS.USER_ACCOUNT.EMAILS.ERRORS.EMAIL_IN_USE`),
            type: "danger",
            lifeTime: 100000,
          });
          break;
        }
        default:
          addMessage({ context: MODAL_MESSENGER_CONTEXT, text: t("common:ERROR_MESSAGES.GENERAL"), type: "warning" });
      }
    }
  }, []);

  const emailOptions = useMemo(() => {
    return [newEmailOption, ...User.sessionUser.emails].filter(e => e).map(e => ({ code: e?.email, value: e?.email }));
  }, [newEmailOption, User.sessionUser.emails]);

  return (
    <Section borderRadius padding={["xl"]} innerMargin="md">
      <Flex justifyContent="space-between">
        <Header weight="bold">{t(`USERS.SETTINGS.USER_ACCOUNT.EMAILS.${variant}_POPUP.HEADER`)}</Header>
        <CloseButton onClick={onClose} />
      </Flex>
      <Flex>
        {variant === "ASSIGN_TO_CONSULTANT" ? undefined : (
          <Text>{t(`USERS.SETTINGS.USER_ACCOUNT.EMAILS.${variant}_POPUP.TEXT`)}</Text>
        )}
      </Flex>
      <Flex>
        <Formik
          innerRef={formRef}
          initialValues={initialValues}
          validationSchema={Yup.object().shape({
            ...(hasEmail
              ? {
                  email: Yup.string()
                    .nullable()
                    .required(VALIDATION_TYPES.REQUIRED)
                    .matches(
                      /^[a-zA-Z\d._-]+@[a-zA-Z\d][a-zA-Z\d-.]{0,61}[a-zA-Z\d]\.[a-zA-Z]{2,}$/,
                      VALIDATION_TYPES.EMAIL,
                    ),
                }
              : {}),
            ...(hasPassword ? { password: Yup.string().required(VALIDATION_TYPES.REQUIRED) } : {}),
          })}
          onSubmit={handleSubmit}>
          {(form: FormikProps<IEmailHandlingFormValues>) => (
            <>
              <FormikForm>
                <Flex flexDirection="column" gap="xl">
                  <Flex flexDirection="column" gap="sm">
                    <Messenger context={MODAL_MESSENGER_CONTEXT} />

                    {hasEmail && (
                      <FormGroup
                        label={<FormLabel label={t("USERS.SETTINGS.USER_ACCOUNT.EMAILS.EMAIL_ADDRESS")} required />}
                        error={
                          <FormikError
                            name="email"
                            data-test="error-email"
                            overrideMessages={EMAIL_OVERRIDE_MESSAGES}
                          />
                        }>
                        {emailVariant === "INPUT" ? (
                          <FormikTextInput size="lg" type="email" name="email" value={form.values.email} required />
                        ) : (
                          <FormikSelect
                            name="email"
                            testName="email"
                            value={form.values.email}
                            required
                            options={emailOptions}
                            size="lg"
                            simpleValue
                            isCreatable
                            placeholder=""
                            onCreateOption={(input: string) => {
                              form.setFieldValue("email", input);
                              setNewEmailOption({ email: input });
                            }}
                            createOptionPosition={"first"}
                            getNewOptionData={(input: string) => ({
                              code: input,
                              value: `${t("USERS.SETTINGS.USER_ACCOUNT.EMAILS.ADD_NEW_EMAIl_LABEL")}: ${input}`,
                            })}
                          />
                        )}
                      </FormGroup>
                    )}
                    {hasPassword && (
                      <div className={`${baseClass}__password`}>
                        <FormGroup
                          label={<FormLabel label={t("USERS.SETTINGS.USER_ACCOUNT.EMAILS.PASSWORD")} required />}
                          error={<FormikError name="password" data-test="error-password" />}>
                          <FormikTextInput
                            size="lg"
                            type="password"
                            name="password"
                            value={form.values.password}
                            required
                          />
                        </FormGroup>
                        <Link
                          className={`${baseClass}__password__forgot`}
                          as={ReactRouterLink}
                          variant="primary"
                          to={router.get("password-reset").path}>
                          {t("USERS.SETTINGS.USER_ACCOUNT.EMAILS.FORGOT_PASSWORD")}
                        </Link>
                      </div>
                    )}
                  </Flex>
                </Flex>
                <Flex flexDirection="column" mt={["md", null]}>
                  <Flex gap="sm" justifyContent="flex-end">
                    <Button type="button" variant="secondary" size="lg" onClick={onClose} disabled={form.isSubmitting}>
                      {t("USERS.SETTINGS.USER_ACCOUNT.EMAILS.CANCEL_BUTTON")}
                    </Button>
                    <Button type="button" size="lg" onClick={form.submitForm} disabled={form.isSubmitting}>
                      {t(`USERS.SETTINGS.USER_ACCOUNT.EMAILS.${variant}_POPUP.SUBMIT_BUTTON`)}
                    </Button>
                  </Flex>
                </Flex>
              </FormikForm>
            </>
          )}
        </Formik>
      </Flex>
    </Section>
  );
};

export const EmailHandlingModal = inDialogModal(EmailHandlingModalComponent);
